import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Search,
  HelpCircle,
  CreditCard,
  Calendar,
  Car,
  Phone,
  Shield,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import "./HelpSection.css";
import { Helmet } from "react-helmet-async";

export function HelpSection() {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const categories = [
    {
      icon: <Calendar />,
      title: t("help.categories.booking.title"),
      description: t("help.categories.booking.description"),
      color: "category-blue",
    },
    {
      icon: <CreditCard />,
      title: t("help.categories.payment.title"),
      description: t("help.categories.payment.description"),
      color: "category-green",
    },
    {
      icon: <Car />,
      title: t("help.categories.vehicles.title"),
      description: t("help.categories.vehicles.description"),
      color: "category-purple",
    },
    {
      icon: <Shield />,
      title: t("help.categories.policies.title"),
      description: t("help.categories.policies.description"),
      color: "category-red",
    },
  ];

  const faqs = t("help.faqs", {
    returnObjects: true,
  }) as {
    category: string;
    question: string;
    answer: string;
  }[];

  const filteredFAQs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <>
    <section className="help-section">
      <div className="help-header">
        <h1 className="help-header-title">
          {t("help.title")}
        </h1>

        <p className="help-header-subtitle">
          {t("help.subtitle")}
        </p>

        <div className="help-search">
          <Search className="help-search-icon" />

          <input
            type="text"
            placeholder={t("help.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="categories-grid">
        {categories.map((category, index) => (
          <div key={index} className="category-card">
            <div className={`category-icon ${category.color}`}>
              {category.icon}
            </div>

            <h3 className="category-title">
              {category.title}
            </h3>

            <p className="category-description">
              {category.description}
            </p>
          </div>
        ))}
      </div>

      <div className="help-faq">
        <h2 className="faq-title-section">
          {t("help.faqTitle")}
        </h2>

        {filteredFAQs.length > 0 ? (
          <div className="faq-list">
            {filteredFAQs.map((faq, index) => (
              <div key={index} className="faq-card">
                <div
                  className="faq-header"
                  onClick={() => toggleFAQ(index)}
                >
                  <span className="faq-category">
                    {faq.category}
                  </span>

                  <span className="faq-question">
                    {faq.question}
                  </span>

                  {expandedFAQ === index ? (
                    <ChevronUp />
                  ) : (
                    <ChevronDown />
                  )}
                </div>

                {expandedFAQ === index && (
                  <div className="faq-content">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="faq-empty">
            <HelpCircle className="faq-empty-icon" />

            <h3>{t("help.noResults")}</h3>

            <p>{t("help.noResultsDescription")}</p>
          </div>
        )}
      </div>

      <div className="help-cta">
        <div className="cta-card">
          <h2>{t("help.cta.title")}</h2>

          <p>{t("help.cta.description")}</p>

          <div className="cta-buttons">
            <button className="cta-button-primary">
              <Phone /> {t("help.cta.call")}
            </button>

            <button className="cta-button-secondary">
              <HelpCircle /> {t("help.cta.message")}
            </button>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}